/**
 * Small file helpers for Service Desk uploads. The api takes attachments as
 * base64 JSON (`{ filename, content_type, base64 }`), not multipart.
 */

import type { SdAttachmentInput } from '@ampersandbrands/ui';

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.replace(/^data:[^;]*;base64,/, ''));
    };
    reader.readAsDataURL(file);
  });
}

export async function fileToAttachment(file: File): Promise<SdAttachmentInput> {
  return {
    filename: file.name || 'attachment',
    content_type: file.type || 'application/octet-stream',
    base64: await fileToBase64(file),
  };
}

/** Images under this size are almost always email signature icons. */
export const THUMBNAIL_MIN_BYTES = 10 * 1024;

/** True when an attachment should show as a thumbnail: an image of 10 KB or more. */
export function isThumbnailCandidate(a: { content_type: string; size_bytes: number }): boolean {
  return a.content_type.toLowerCase().startsWith('image/') && a.size_bytes >= THUMBNAIL_MIN_BYTES;
}
